import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { alertRecipientsKeys } from './useAlertRecipients';

export interface AlertAcknowledgement {
  id: string;
  userGuid: string;
  dgptAlertEventInstanceId: string;
  dgptAlertRunId: string;
  acknowledgedAt: string | null;
  isValid: boolean;
  createdAt: string;
}

export interface ApplyAcknowledgementPayload {
  acknowledgementId: string;
}

export const keys = {
  base: ['acknowledgements'] as const,
  byEventInstanceId: (eventInstanceId: string) =>
    [...keys.base, eventInstanceId] as const,
} as const;

// Fetch acknowledgements for an event instance
export const useAlertAcknowledgements = (eventInstanceId: string) => {
  return useQuery({
    queryKey: keys.byEventInstanceId(eventInstanceId),
    queryFn: async () => {
      const response = await axios.get<AlertAcknowledgement[]>(
        `${config.apiGateway.dgptAlerts}/acknowledgements/event/${eventInstanceId}`
      );
      return response.data;
    },
    enabled: !!eventInstanceId,
  });
};

// Apply acknowledgement
export const useApplyAcknowledgement = () => {
  const queryClient = useQueryClient();

  return useMutation<AlertAcknowledgement, Error, ApplyAcknowledgementPayload>({
    mutationFn: async (data) => {
      const response = await axios.put(
        `${config.apiGateway.dgptAlerts}/acknowledgements/apply`,
        data
      );
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate the acknowledgements query for the event instance
      queryClient.invalidateQueries({
        queryKey: keys.byEventInstanceId(data.dgptAlertEventInstanceId),
      });

      // Also invalidate the recipients query which shows acknowledgment status
      queryClient.invalidateQueries({
        queryKey: alertRecipientsKeys.byEventInstanceId(
          data.dgptAlertEventInstanceId
        ),
      });

      toast.success('Acknowledgement applied successfully.');
    },
    onError: (error) => {
      console.error('Error applying acknowledgement:', error);
      toast.error('Failed to apply acknowledgement.');
    },
  });
};
